import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, Leaf, Shield, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProjectGallery } from "@/components/ProjectGallery";

const services = [
  {
    title: "Landscape Installation & Maintenance",
    description: "Complete lawn care, garden design, planting, and seasonal maintenance for residential and commercial properties.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/cz3w10y4_Screenshot%202026-02-25%20at%203.46.31%E2%80%AFPM.png",
    alt: "Professional landscape installation in Joliet IL"
  },
  {
    title: "Paver Patios, Walkways & Driveways",
    description: "Custom stone paver installations that enhance your outdoor living space with durable, beautiful designs.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/dx0a3wdq_IMG_4852.jpeg",
    alt: "Paver patio installation in New Lenox IL"
  },
  {
    title: "Retaining Walls",
    description: "Structural and decorative retaining walls that prevent erosion and add dimension to your landscape.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/ja35jvxr_Screenshot%202026-02-25%20at%203.46.46%E2%80%AFPM.png",
    alt: "Retaining wall construction in Frankfort IL"
  },
  {
    title: "Concrete Services",
    description: "Professional concrete driveways, patios, sidewalks, and custom flatwork for lasting results.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/4kmk778h_Screenshot%202026-02-25%20at%203.47.03%E2%80%AFPM.png",
    alt: "Concrete driveway contractor in Naperville IL"
  },
  {
    title: "Snow & Ice Management",
    description: "Reliable residential and commercial snow removal and ice management throughout the winter season.",
    image: "https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/1l72nh65_IMG_0205.jpeg",
    alt: "Snow removal services in Joliet IL"
  }
];

const trustPoints = [
  { icon: Shield, text: "Licensed & Insured" },
  { icon: Clock, text: "Reliable Service" },
  { icon: Leaf, text: "Quality Materials" }
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/51zgzflw_Screenshot%202026-02-25%20at%203.46.56%E2%80%AFPM.png"
            alt="Professional landscaping by J Guzman Landscaping in Joliet IL"
            className="w-full h-full object-cover"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <p className="text-[#2F5930] font-semibold mb-4 tracking-wide uppercase text-sm animate-fade-up">
              Joliet&apos;s Trusted Landscaping Experts
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1F2937] leading-tight mb-6 font-['Manrope'] animate-fade-up stagger-1">
              J Guzman Landscaping LLC
            </h1>
            <p className="text-lg md:text-xl text-[#4B5563] mb-8 leading-relaxed animate-fade-up stagger-2">
              Transform your outdoor space with expert landscaping, paver patios, retaining walls, 
              concrete services, and snow removal. Serving Joliet, New Lenox, Frankfort, Naperville, 
              and all of Will County.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10 animate-fade-up stagger-3">
              <a href="tel:8159098790">
                <Button 
                  size="lg" 
                  className="bg-[#2F5930] hover:bg-[#244525] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-[#2F5930] border-2 border-[#2F5930] hover:bg-[#F0FDF4] rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="hero-estimate-btn"
                >
                  Request a Free Estimate
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Trust Points */}
            <div className="flex flex-wrap gap-6 animate-fade-up stagger-4">
              {trustPoints.map((point, index) => (
                <div key={index} className="flex items-center gap-2 text-[#4B5563]">
                  <point.icon className="w-5 h-5 text-[#2F5930]" />
                  <span className="font-medium">{point.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#2F5930] font-semibold mb-3 tracking-wide uppercase text-sm">
              What We Offer
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-4 font-['Manrope']">
              Complete Landscaping Solutions
            </h2>
            <p className="text-lg text-[#4B5563] max-w-2xl mx-auto">
              From landscape design to snow removal, we provide comprehensive outdoor services 
              for homes and businesses throughout Illinois.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <article 
                key={index} 
                className="service-card group"
                data-testid={`service-card-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#1F2937] mb-3 font-['Manrope']">
                    {service.title}
                  </h3>
                  <p className="text-[#4B5563] leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Link 
                    to="/gallery" 
                    className="inline-flex items-center text-[#2F5930] font-medium hover:underline group"
                  >
                    See More Examples
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                size="lg"
                className="bg-[#2F5930] hover:bg-[#244525] text-white rounded-full px-8"
                data-testid="view-services-btn"
              >
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Project Gallery Slideshow */}
      <ProjectGallery />

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-[#F9FAFB]" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[#2F5930] font-semibold mb-3 tracking-wide uppercase text-sm">
                Why Choose Us
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-6 font-['Manrope']">
                Joliet&apos;s Landscaping Company You Can Trust
              </h2>
              <p className="text-lg text-[#4B5563] mb-8 leading-relaxed">
                J Guzman Landscaping LLC has built a reputation for quality workmanship, 
                reliable service, and attention to detail. We treat every property like our own.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  "Experienced team with years of local expertise",
                  "Quality materials and professional equipment",
                  "Free estimates with transparent pricing",
                  "Serving residential and commercial clients",
                  "Year-round services including snow removal"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#2F5930] flex-shrink-0 mt-0.5" />
                    <span className="text-[#4B5563]">{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-[#2F5930] hover:bg-[#244525] text-white rounded-full px-8"
                  data-testid="get-estimate-btn"
                >
                  Get Your Free Estimate
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_guzman-landscaping/artifacts/tpx083x2_Screenshot%202026-02-25%20at%203.46.39%E2%80%AFPM.png"
                alt="Professional lawn care by J Guzman Landscaping in Joliet IL"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#2F5930] text-white p-6 rounded-2xl shadow-lg hidden md:block">
                <p className="text-3xl font-bold font-['Manrope']">10+</p>
                <p className="text-sm opacity-90">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Preview */}
      <section className="py-20 md:py-28 bg-white" data-testid="service-areas-preview">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
          <p className="text-[#2F5930] font-semibold mb-3 tracking-wide uppercase text-sm">
            Areas We Serve
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-6 font-['Manrope']">
            Landscaping Services Throughout Will County
          </h2>
          <p className="text-lg text-[#4B5563] mb-10 max-w-2xl mx-auto">
            Proudly serving Joliet and surrounding communities with professional landscaping, 
            paver installation, and snow removal services.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {[
              "Joliet", "New Lenox", "Frankfort", "Manhattan", "Orland Park",
              "Homer Glen", "Naperville", "Aurora", "Shorewood", "Morris"
            ].map((city) => (
              <span key={city} className="area-badge">
                {city}, IL
              </span>
            ))}
          </div>

          <Link to="/service-areas">
            <Button 
              variant="outline"
              size="lg"
              className="text-[#2F5930] border-2 border-[#2F5930] hover:bg-[#F0FDF4] rounded-full px-8"
              data-testid="view-areas-btn"
            >
              View All Service Areas
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-20 md:py-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Manrope']">
            Ready to Transform Your Outdoor Space?
          </h2>
          <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
            Contact J Guzman Landscaping today for a free estimate. We&apos;ll help bring your 
            landscaping vision to life.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:8159098790">
              <Button 
                size="lg"
                className="bg-white text-[#2F5930] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
                data-testid="cta-call-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                (815) 909-8790
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-2 border-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
                data-testid="cta-contact-btn"
              >
                Get Free Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Local Business Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "J Guzman Landscaping LLC",
          "image": "https://images.unsplash.com/photo-1587716839025-07c325222549",
          "telephone": "815-909-8790",
          "email": "Jguzmanlandscaping01@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Joliet",
            "addressRegion": "IL",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 41.5250,
            "longitude": -88.0817
          },
          "url": "https://jguzmanlandscaping.com",
          "priceRange": "$$",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            "opens": "07:00",
            "closes": "17:00"
          },
          "sameAs": [],
          "areaServed": [
            "Joliet, IL", "New Lenox, IL", "Frankfort, IL", "Manhattan, IL",
            "Orland Park, IL", "Homer Glen, IL", "Naperville, IL", "Aurora, IL"
          ],
          "serviceType": [
            "Landscaping", "Paver Installation", "Retaining Walls", 
            "Concrete Services", "Snow Removal"
          ]
        })
      }} />
    </div>
  );
}
